import { useRef, useEffect, useCallback } from 'react';
import './PromptEditor.css';

/**
 * Known variables that the backend resolves at document generation time.
 * Sourced from case_service.py's variable_context.
 */
const KNOWN_VARIABLES = ['verified_facts', 'timeline_summary'];

/**
 * Reusable prompt textarea with {variable} highlighting and stats.
 *
 * Renders a transparent textarea over a backdrop div that highlights
 * {variable} tokens: teal for known/resolvable variables, red for unknown.
 */
export default function PromptEditor({
  value = '',
  onChange,
  rows = 6,
  placeholder = 'Enter prompt...',
  variables = KNOWN_VARIABLES,
  showStats = true,
  maxHeight = 600,
  label,
  helpText,
  readOnly = false,
}) {
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);

  // Auto-resize textarea to fit content
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
  }, [maxHeight]);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  // Sync scroll between textarea and backdrop
  const handleScroll = () => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // Build highlighted HTML for the backdrop
  const highlightedHtml = value.replace(
    /\{(\w+)\}/g,
    (match, varName) => {
      const isKnown = variables.includes(varName);
      const cls = isKnown ? 'pe-var-known' : 'pe-var-unknown';
      return `<span class="${cls}">${match}</span>`;
    }
  );

  // Compute stats
  const charCount = value.length;
  const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0;
  const tokenEstimate = Math.ceil(charCount / 4);

  return (
    <div className="pe-wrapper">
      {label && (
        <label className="pe-label">
          {label}
          {helpText && <span className="pe-label-hint"> &mdash; {helpText}</span>}
        </label>
      )}
      <div className="pe-container">
        <div
          ref={backdropRef}
          className="pe-backdrop"
          style={{ maxHeight }}
          dangerouslySetInnerHTML={{ __html: highlightedHtml + '\n' }}
        />
        <textarea
          ref={textareaRef}
          className="pe-textarea"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onScroll={handleScroll}
          onInput={autoResize}
          rows={rows}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{ maxHeight }}
        />
      </div>
      {showStats && (
        <div className="pe-stats">
          <span>{charCount.toLocaleString()} chars</span>
          <span>{wordCount} words</span>
          <span>~{tokenEstimate} tokens</span>
        </div>
      )}
    </div>
  );
}
